/* ==========================================================================
   VELOURÉA 4.x · theme script
   Vanilla JS + custom elements. No dependencies.
   ========================================================================== */
(() => {
  'use strict';

  const V = (window.V = window.V || {});
  V.strings = V.strings || {};
  V.routes = V.routes || { root: '/', cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change', cartUpdate: '/cart/update', predictive: '/search/suggest' };

  /* ---------- utils ---------- */
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches || document.documentElement.classList.contains('a11y-still');
  const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;
  const debounce = (fn, ms = 250) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  const store = {
    get(k, d) { try { const v = localStorage.getItem(k); return v === null ? d : JSON.parse(v); } catch (e) { return d; } },
    set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private mode */ } }
  };
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  // Scroll a full-width snap track to slide i (RTL: scrollLeft is 0 → negative)
  const scrollToSlide = (track, i) => {
    const rtl = getComputedStyle(track).direction === 'rtl';
    track.scrollTo({ left: (rtl ? -1 : 1) * i * track.clientWidth, behavior: reducedMotion() ? 'auto' : 'smooth' });
  };

  V.formatMoney = (cents, format = V.moneyFormat || '₪{{amount}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const n = Number(cents) / 100;
    const fmt = (num, dec, th = ',', ds = '.') => {
      const parts = num.toFixed(dec).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, th);
      return parts.join(ds);
    };
    const out = format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(n, 0);
        case 'amount_with_comma_separator': return fmt(n, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(n, 0, '.');
        default: return fmt(n, 2);
      }
    });
    return out.replace(/([.,])00(?!\d)/, '');
  };

  let toastTimer;
  V.toast = (msg) => {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    el.style.animation = 'none'; void el.offsetWidth; el.style.animation = '';
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { el.hidden = true; }, 2600);
  };

  /* ---------- dialogs / drawers (native <dialog>: focus containment + Esc) ---------- */
  const Dialogs = {
    openers: new WeakMap(),
    open(dialog, opener) {
      if (!dialog || dialog.open) return;
      if (opener) this.openers.set(dialog, opener);
      dialog.classList.remove('is-closing');
      dialog.showModal();
      document.documentElement.classList.add('is-locked');
      dialog.dispatchEvent(new CustomEvent('dialog:open'));
      const auto = dialog.querySelector('[autofocus], input[type="search"]');
      if (auto && isDesktop()) auto.focus();
    },
    close(dialog) {
      if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;
      const done = () => {
        dialog.classList.remove('is-closing');
        dialog.close();
        if (!$$('dialog[open]').length) document.documentElement.classList.remove('is-locked');
        const opener = this.openers.get(dialog);
        if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
        dialog.dispatchEvent(new CustomEvent('dialog:close'));
      };
      if (reducedMotion()) return done();
      dialog.classList.add('is-closing');
      let finished = false;
      const end = () => { if (!finished) { finished = true; done(); } };
      dialog.addEventListener('animationend', end, { once: true });
      setTimeout(end, 350);
    }
  };
  V.dialogs = Dialogs;

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open]');
    if (opener) {
      const d = document.getElementById(opener.dataset.open);
      if (d) { e.preventDefault(); Dialogs.open(d, opener); }
      return;
    }
    const closer = e.target.closest('[data-close]');
    if (closer) { Dialogs.close(closer.closest('dialog')); return; }
    // click on the backdrop
    if (e.target.tagName === 'DIALOG' && e.target.hasAttribute('data-dialog')) {
      const r = e.target.getBoundingClientRect();
      const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (!inside || e.target.classList.contains('search-dialog') || e.target.classList.contains('zoom')) Dialogs.close(e.target);
    }
  });
  document.addEventListener('cancel', (e) => {
    if (e.target.tagName === 'DIALOG') { e.preventDefault(); Dialogs.close(e.target); }
  }, true);

  /* ---------- header ---------- */
  class VHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.header-section') || this;
      this.transparent = this.classList.contains('site-header--transparent');
      const first = $('#MainContent > .shopify-section:first-child');
      this.hasHero = !!(first && first.querySelector('.hero'));
      if (this.transparent && !this.hasHero) this.classList.remove('site-header--transparent');
      this.lastY = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      this.initMega();
      const setH = () => document.documentElement.style.setProperty('--header-h', `${$('.site-header__bar', this).offsetHeight}px`);
      setH(); window.addEventListener('resize', debounce(setH, 150));
    }
    onScroll() {
      const y = window.scrollY;
      const solid = y > 12 || !this.transparent;
      this.classList.toggle('is-solid', solid);
      if (this.dataset.hideOnScroll === 'true') {
        const down = y > this.lastY && y > 160;
        const anyOpen = $('.nav-capsule__item.is-open', this);
        this.classList.toggle('is-hidden', down && !anyOpen);
        document.body.classList.toggle('header-hidden', down && !anyOpen);
      }
      this.lastY = y;
    }
    initMega() {
      $$('[data-mega]', this).forEach((item) => {
        const btn = $('button', item);
        let t;
        const open = () => { clearTimeout(t); this.closeAll(item); item.classList.add('is-open'); btn.setAttribute('aria-expanded', 'true'); };
        const close = () => { item.classList.remove('is-open'); btn.setAttribute('aria-expanded', 'false'); };
        btn.addEventListener('click', () => (item.classList.contains('is-open') ? close() : open()));
        item.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') open(); });
        item.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') t = setTimeout(close, 180); });
        item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) close(); });
        item.addEventListener('keydown', (e) => { if (e.key === 'Escape') { close(); btn.focus(); } });
      });
    }
    closeAll(except) {
      $$('.nav-capsule__item.is-open', this).forEach((i) => { if (i !== except) { i.classList.remove('is-open'); $('button', i).setAttribute('aria-expanded', 'false'); } });
    }
  }
  customElements.define('v-header', VHeader);

  /* ---------- announcement ---------- */
  class VAnnouncement extends HTMLElement {
    connectedCallback() {
      this.items = $$('.announcement__item', this);
      if (this.items.length < 2) return;
      this.i = 0;
      const ms = (Number(this.dataset.speed) || 5) * 1000;
      this.timer = setInterval(() => {
        if (reducedMotion() || (isDesktop() && this.classList.contains('announcement--inline'))) return;
        this.items[this.i].hidden = true;
        this.i = (this.i + 1) % this.items.length;
        this.items[this.i].hidden = false;
      }, ms);
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('v-announcement', VAnnouncement);

  /* ---------- hero slider ---------- */
  class VHero extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-track]', this);
      this.slides = $$('[data-slide]', this);
      this.dots = $$('[data-dot]', this);
      this.index = 0;
      if (this.slides.length < 2) return;
      this.dots.forEach((d) => d.addEventListener('click', () => { this.go(Number(d.dataset.dot)); this.restart(); }));
      this.track.addEventListener('scroll', debounce(() => this.sync(), 60), { passive: true });
      const pause = $('[data-pause]', this);
      this.paused = reducedMotion();
      if (pause) {
        pause.addEventListener('click', () => {
          this.paused = !this.paused;
          pause.setAttribute('aria-pressed', String(this.paused));
          pause.innerHTML = this.paused
            ? '<svg class="icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 4.5v15l12-7.5-12-7.5Z" fill="currentColor"/></svg>'
            : '<svg class="icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 4h3.5v16H7zM13.5 4H17v16h-3.5z" fill="currentColor"/></svg>';
        });
      }
      this.addEventListener('pointerenter', () => { this.hover = true; });
      this.addEventListener('pointerleave', () => { this.hover = false; });
      this.addEventListener('focusin', () => { this.hover = true; });
      this.addEventListener('focusout', () => { this.hover = false; });
      this.restart();
    }
    restart() {
      clearInterval(this.timer);
      if (this.dataset.autoplay !== 'true') return;
      this.timer = setInterval(() => { if (!this.paused && !this.hover && !document.hidden) this.go((this.index + 1) % this.slides.length); }, (Number(this.dataset.speed) || 6) * 1000);
    }
    go(i) {
      if (!this.slides[i]) return;
      scrollToSlide(this.track, i);
      this.setActive(i);
    }
    sync() {
      const w = this.track.clientWidth;
      const i = Math.round(Math.abs(this.track.scrollLeft) / w);
      this.setActive(Math.min(i, this.slides.length - 1));
    }
    setActive(i) {
      this.index = i;
      this.dots.forEach((d, n) => { const dot = $('.hero__dot', d); if (dot) dot.setAttribute('aria-current', String(n === i)); });
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('v-hero', VHero);

  /* ---------- product gallery + zoom ---------- */
  class VGallery extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-track]', this);
      this.slides = $$('[data-slide]', this);
      this.thumbs = $$('[data-thumb]', this);
      this.dots = $$('.gallery__dots span', this);
      this.counter = $('[data-current]', this);
      this.zoom = $('[data-zoom-dialog]', this);
      if (!this.track) return;
      this.track.addEventListener('scroll', debounce(() => this.sync(), 50), { passive: true });
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.thumb))));
      const prev = $('[data-prev]', this); const next = $('[data-next]', this);
      if (prev) prev.addEventListener('click', () => this.go(Math.max(0, this.index - 1)));
      if (next) next.addEventListener('click', () => this.go(Math.min(this.slides.length - 1, this.index + 1)));
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.go(Math.min(this.slides.length - 1, this.index + 1));
        if (e.key === 'ArrowRight') this.go(Math.max(0, this.index - 1));
      });
      this.index = 0;
      if (this.zoom) {
        const openZoom = () => this.openZoom();
        const zb = $('[data-zoom-open]', this);
        if (zb) zb.addEventListener('click', openZoom);
        this.slides.forEach((s) => { if (!s.classList.contains('gallery__slide--video')) s.addEventListener('click', openZoom); });
        $$('[data-zoom-item]', this.zoom).forEach((item) => {
          item.addEventListener('click', (e) => {
            const zoomed = item.classList.toggle('is-zoomed');
            if (zoomed) {
              const img = $('img', item);
              requestAnimationFrame(() => {
                const rx = e.offsetX / (img.clientWidth / 2 || 1); const ry = e.offsetY / (img.clientHeight / 2 || 1);
                item.scrollTo({ left: (item.scrollWidth - item.clientWidth) * Math.min(1, rx / 2) * (document.dir === 'rtl' ? -1 : 1), top: (item.scrollHeight - item.clientHeight) * Math.min(1, ry / 2) });
              });
            }
          });
        });
      }
    }
    go(i) {
      if (!this.slides[i]) return;
      scrollToSlide(this.track, i);
      this.setActive(i);
    }
    goToMedia(id) {
      const s = this.slides.find((x) => x.dataset.mediaId === String(id));
      if (s) this.go(Number(s.dataset.slide));
    }
    sync() {
      const w = this.track.clientWidth || 1;
      this.setActive(Math.min(this.slides.length - 1, Math.round(Math.abs(this.track.scrollLeft) / w)));
    }
    setActive(i) {
      this.index = i;
      if (this.counter) this.counter.textContent = String(i + 1);
      this.thumbs.forEach((t, n) => t.setAttribute('aria-current', String(n === i)));
      this.dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
    }
    openZoom() {
      Dialogs.open(this.zoom, document.activeElement);
      const item = $(`[data-zoom-item="${this.index}"]`, this.zoom) || $('[data-zoom-item]', this.zoom);
      const items = $$('[data-zoom-item]', this.zoom);
      if (item) requestAnimationFrame(() => scrollToSlide($('[data-zoom-track]', this.zoom), Math.max(0, items.indexOf(item))));
    }
  }
  customElements.define('v-gallery', VGallery);

  /* ---------- cart ---------- */
  const Cart = {
    sections: () => ($('#CartDrawer') ? ['cart-drawer'] : []),
    async request(url, body) {
      const sections = this.sections();
      if (sections.length) { body.sections = sections; body.sections_url = window.location.pathname; }
      const res = await fetch(`${url}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || V.strings.error);
      return data;
    },
    async add(items) {
      const data = await this.request(V.routes.cartAdd, { items });
      await this.afterChange(data, true);
      return data;
    },
    async change(key, quantity) {
      const data = await this.request(V.routes.cartChange, { id: key, quantity });
      await this.afterChange(data, false);
      return data;
    },
    async afterChange(data, added) {
      const cart = data.item_count !== undefined ? data : await (await fetch(`${V.routes.cart}.js`)).json();
      this.setCount(cart.item_count);
      if (data.sections && data.sections['cart-drawer']) this.renderDrawer(data.sections['cart-drawer']);
      else if (added || $('#CartDrawer')) await this.refreshDrawer();
      if (document.body.classList.contains('template-cart')) await this.refreshPage();
      if (added) {
        if (V.cartType === 'page') { window.location.href = V.routes.cart; return; }
        const d = $('#CartDrawer');
        const others = $$('dialog[open]').filter((x) => x.id !== 'CartDrawer');
        others.forEach((x) => Dialogs.close(x));
        if (d) setTimeout(() => Dialogs.open(d), others.length ? 300 : 0);
        else V.toast(V.strings.added);
      }
    },
    setCount(n) {
      $$('[data-cart-count]').forEach((b) => {
        b.textContent = n; b.dataset.count = n;
        b.classList.remove('is-bump'); void b.offsetWidth; b.classList.add('is-bump');
      });
      $$('[data-cart-label]').forEach((l) => { l.textContent = l.textContent.replace(/\d+/, n); });
    },
    renderDrawer(html) {
      const fresh = $('#CartDrawer', parseHTML(html));
      const cur = $('#CartDrawer');
      if (fresh && cur) {
        const scroll = $('.drawer__body', cur) ? $('.drawer__body', cur).scrollTop : 0;
        cur.innerHTML = fresh.innerHTML;
        const body = $('.drawer__body', cur); if (body) body.scrollTop = scroll;
      }
    },
    async refreshDrawer() {
      if (!$('#CartDrawer')) return;
      const res = await fetch(`${window.location.pathname}?sections=cart-drawer`);
      const json = await res.json();
      if (json['cart-drawer']) this.renderDrawer(json['cart-drawer']);
    },
    async refreshPage() {
      const html = await (await fetch(window.location.href)).text();
      const main = $('#MainContent', parseHTML(html));
      if (main) $('#MainContent').innerHTML = main.innerHTML;
    }
  };
  V.cart = Cart;

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const step = e.target.closest('[data-qty-step]');
        const rm = e.target.closest('[data-remove]');
        if (step) {
          const input = $('[data-qty-input]', step.closest('[data-qty]'));
          input.value = Math.max(0, Number(input.value) + Number(step.dataset.qtyStep));
          this.update(input);
        } else if (rm) {
          e.preventDefault();
          this.update(null, rm.closest('[data-key]'), 0);
        }
      });
      this.addEventListener('change', (e) => { if (e.target.matches('[data-qty-input]')) this.update(e.target); });
    }
    async update(input, row, qty) {
      row = row || input.closest('[data-key]');
      const q = qty !== undefined ? qty : Math.max(0, parseInt(input.value, 10) || 0);
      row.classList.add('is-updating');
      try { await Cart.change(row.dataset.key, q); } catch (err) { V.toast(err.message); row.classList.remove('is-updating'); }
    }
  }
  customElements.define('cart-items', CartItems);

  document.addEventListener('click', (e) => {
    const openCart = e.target.closest('[data-cart-open]');
    if (openCart && V.cartType !== 'page' && $('#CartDrawer') && !document.body.classList.contains('template-cart')) {
      e.preventDefault(); Dialogs.open($('#CartDrawer'), openCart); return;
    }
    const qa = e.target.closest('[data-quick-add]');
    if (qa) {
      e.preventDefault();
      qa.classList.add('is-loading'); qa.setAttribute('aria-busy', 'true');
      Cart.add([{ id: Number(qa.dataset.quickAdd), quantity: 1 }])
        .catch((err) => V.toast(err.message))
        .finally(() => { qa.classList.remove('is-loading'); qa.removeAttribute('aria-busy'); });
      return;
    }
    const qv = e.target.closest('[data-quick-view]');
    if (qv) { e.preventDefault(); QuickView.open(qv.dataset.quickView, qv); }
  });
  document.addEventListener('input', debounce((e) => {
    if (!e.target.matches('[data-cart-note]')) return;
    fetch(`${V.routes.cartUpdate}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: e.target.value }) });
  }, 500));

  /* ---------- quick view ---------- */
  const QuickView = {
    async open(url, opener) {
      const dialog = $('#QuickView');
      if (!dialog) { window.location.href = url; return; }
      const body = $('[data-quick-view-body]', dialog);
      body.innerHTML = '<div class="qv-loading"><span class="spinner"></span></div>';
      Dialogs.open(dialog, opener);
      try {
        const clean = url.split('?')[0];
        const html = await (await fetch(`${clean}?section_id=quick-view`)).text();
        const doc = parseHTML(html);
        const qv = $('.qv', doc);
        body.innerHTML = qv ? qv.outerHTML : '';
        const first = $('input, button', body);
        if (first && isDesktop()) first.focus();
      } catch (e) { window.location.href = url; }
    }
  };

  /* ---------- product form / variant picker ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      if (!this.form) return;
      const json = $('[data-variants]', this);
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.idInput = $('[data-variant-id]', this);
      this.atc = $('[data-atc]', this);
      this.atcLabel = $('[data-atc-label]', this);
      this.fieldsets = $$('[data-option]', this);
      this.root = this.closest('[data-product-page], .qv') || document;
      this.variant = this.idInput.value ? this.variants.find((v) => String(v.id) === this.idInput.value) : null;
      if (!this.fieldsets.length && this.variants.length) this.variant = this.variants[0];
      this.addEventListener('change', (e) => { if (e.target.name && e.target.name.startsWith('option-')) this.onOption(); });
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      const buy = $('[data-buy-now]', this);
      if (buy) buy.addEventListener('click', () => this.onSubmit(null, true));
      this.updateAvailability();
    }
    selected() { return this.fieldsets.map((f) => { const c = $('input:checked', f); return c ? c.value : null; }); }
    onOption() {
      const sel = this.selected();
      this.fieldsets.forEach((f, i) => {
        const l = $('[data-selected-label]', f); if (l) l.textContent = sel[i] || '';
        if (sel[i]) { f.classList.remove('opt--error'); const er = $('[data-opt-error]', f); if (er) er.hidden = true; }
      });
      this.updateAvailability();
      if (sel.some((v) => v === null)) { this.variant = null; this.idInput.value = ''; this.setButton(); return; }
      this.variant = this.variants.find((v) => v.options.every((o, i) => o === sel[i])) || null;
      this.idInput.value = this.variant ? this.variant.id : '';
      this.setButton();
      if (!this.variant) return;
      this.updatePrice();
      if (!this.hasAttribute('data-quick') && this.variant.featured_media) {
        const g = $('v-gallery', this.root); if (g && g.goToMedia) g.goToMedia(this.variant.featured_media.id);
      }
      if (!this.hasAttribute('data-quick')) {
        const url = new URL(window.location.href); url.searchParams.set('variant', this.variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: this.variant, form: this.form.id } }));
    }
    updateAvailability() {
      const sel = this.selected();
      this.fieldsets.forEach((f, i) => {
        $$('input', f).forEach((input) => {
          const ok = this.variants.some((v) => v.available && v.options[i] === input.value && v.options.every((o, j) => j === i || sel[j] === null || sel[j] === o));
          input.closest('.size-pill').classList.toggle('is-unavailable', !ok);
        });
      });
    }
    setButton() {
      if (!this.atc) return;
      if (!this.variant) { this.atc.disabled = false; this.atc.setAttribute('aria-disabled', 'false'); this.atcLabel.textContent = V.strings.chooseSize; return; }
      if (!this.variant.available) { this.atc.disabled = true; this.atcLabel.textContent = V.strings.soldOut; return; }
      this.atc.disabled = false; this.atcLabel.textContent = V.strings.addToCart;
    }
    updatePrice() {
      const v = this.variant;
      $$('[data-price]', this.root).forEach((p) => {
        if (p.closest('.card, .mini-card')) return;
        const cur = $('.price__current', p); if (cur) cur.textContent = V.formatMoney(v.price);
        const cmp = $('.price__compare', p);
        const sale = v.compare_at_price && v.compare_at_price > v.price;
        p.classList.toggle('price--sale', !!sale);
        if (cmp) { cmp.hidden = !sale; if (sale) cmp.textContent = V.formatMoney(v.compare_at_price); }
        const save = $('.price__save', p);
        if (save) {
          save.hidden = !sale;
          if (sale) {
            const diff = v.compare_at_price - v.price;
            save.textContent = save.textContent.replace(/[\d.,]+(?=[^\d]*\()/, (V.formatMoney(diff).match(/[\d.,]+/) || [''])[0].replace(/\.00$/, '')).replace(/\(\d+%\)/, `(${Math.round((diff * 100) / v.compare_at_price)}%)`);
          }
        }
      });
    }
    validate() {
      const sel = this.selected();
      let ok = true;
      this.fieldsets.forEach((f, i) => {
        if (sel[i] === null) {
          ok = false;
          f.classList.remove('opt--error'); void f.offsetWidth; f.classList.add('opt--error');
          const er = $('[data-opt-error]', f); if (er) er.hidden = false;
        }
      });
      if (!ok) {
        const first = $('.opt--error', this);
        if (first) { first.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' }); const inp = $('input', first); if (inp) inp.focus({ preventScroll: true }); }
      }
      return ok && !!this.variant;
    }
    async onSubmit(e, buyNow = false) {
      if (e) e.preventDefault();
      if (!this.validate()) return;
      const err = $('[data-form-error]', this); if (err) err.hidden = true;
      this.atc.classList.add('is-loading');
      try {
        if (buyNow) {
          await fetch(`${V.routes.cartAdd}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: this.variant.id, quantity: 1 }] }) });
          window.location.href = '/checkout';
          return;
        }
        const fd = new FormData(this.form);
        const props = {};
        for (const [k, v] of fd.entries()) { const m = k.match(/^properties\[(.+)\]$/); if (m && v) props[m[1]] = v; }
        await Cart.add([{ id: this.variant.id, quantity: Number(fd.get('quantity') || 1), properties: props }]);
      } catch (ex) {
        if (err) { err.textContent = ex.message; err.hidden = false; }
      } finally { this.atc.classList.remove('is-loading'); }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- sticky add to cart ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const form = document.getElementById(this.dataset.form);
      this.pf = form ? form.closest('product-form') : null;
      const target = form ? $('[data-atc]', form) : null;
      const btn = $('[data-sticky-btn]', this);
      if (!target || !btn) return;
      btn.addEventListener('click', () => {
        if (this.pf && this.pf.variant) this.pf.onSubmit(null);
        else if (this.pf) this.pf.validate();
      });
      const io = new IntersectionObserver(([en]) => {
        const show = !en.isIntersecting && en.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', show);
        this.setAttribute('aria-hidden', String(!show));
        btn.tabIndex = show ? 0 : -1;
        document.body.classList.toggle('sticky-atc-on', show);
      });
      io.observe(target);
      document.addEventListener('variant:change', (e) => {
        const v = e.detail.variant; const cur = $('.price__current', this);
        if (cur) cur.textContent = V.formatMoney(v.price);
      });
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ---------- promotion countdown ---------- */
  const pad = (n) => String(n).padStart(2, '0');
  class VCountdown extends HTMLElement {
    connectedCallback() {
      this.end = new Date(this.dataset.end).getTime();
      this.out = $('[data-time]', this);
      if (!this.end || Number.isNaN(this.end)) { this.hidden = true; return; }
      this.tick(); this.timer = setInterval(() => this.tick(), 1000);
    }
    tick() {
      const d = this.end - Date.now();
      if (d <= 0) { this.hidden = true; clearInterval(this.timer); return; }
      const days = Math.floor(d / 864e5); const h = Math.floor((d % 864e5) / 36e5); const m = Math.floor((d % 36e5) / 6e4); const s = Math.floor((d % 6e4) / 1e3);
      this.out.textContent = `${days > 0 ? `${days}d ` : ''}${pad(h)}:${pad(m)}:${pad(s)}`;
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('v-countdown', VCountdown);

  /* ---------- Shabbat timer (password page) ---------- */
  class ShabbatTimer extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute('data-auto')) return; // started by <shabbat-auto> with the computed end
      const exact = (this.dataset.end || '').trim();
      const hours = Number(this.dataset.hours) || 25;
      let end = exact ? new Date(exact).getTime() : NaN;
      if (Number.isNaN(end)) {
        // One key per Shabbat: stamped with the date of the most recent Friday
        const now = new Date();
        const fri = new Date(now); fri.setDate(now.getDate() - ((now.getDay() - 5 + 7) % 7));
        const key = `velourea_shabbat_${fri.getFullYear()}-${fri.getMonth() + 1}-${fri.getDate()}`;
        let start;
        try { start = Number(localStorage.getItem(key)); } catch (e) { start = 0; }
        if (!start) { start = Date.now(); try { localStorage.setItem(key, String(start)); } catch (e) { /* noop */ } }
        end = start + hours * 36e5;
      }
      this.start(end);
    }
    start(end) {
      clearInterval(this.timer);
      this.end = end;
      this.h = $('[data-h]', this); this.m = $('[data-m]', this); this.s = $('[data-s]', this);
      this.tick(); this.timer = setInterval(() => this.tick(), 1000);
    }
    tick() {
      const d = this.end - Date.now();
      if (d <= 0) {
        clearInterval(this.timer);
        $('[data-clock]', this).hidden = true; $('[data-label]', this).hidden = true; $('[data-done]', this).hidden = false;
        this.dispatchEvent(new CustomEvent('shabbat:end', { bubbles: true }));
        return;
      }
      this.h.textContent = pad(Math.floor(d / 36e5)); this.m.textContent = pad(Math.floor((d % 36e5) / 6e4)); this.s.textContent = pad(Math.floor((d % 6e4) / 1e3));
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('shabbat-timer', ShabbatTimer);

  /* ---------- automatic Shabbat closure (sunset in Israel, no external service) ---------- */
  // NOAA-style sunset (±1–2 min), returns a UTC timestamp for the civil date y-m-d at lat/lng
  const sunsetUTC = (y, m, d, lat, lng) => {
    const rad = Math.PI / 180;
    const jd = Date.UTC(y, m - 1, d, 12) / 864e5 + 2440587.5;
    const n = Math.round(jd - 2451545 + 0.0008);
    const js = n - lng / 360;
    const M = (357.5291 + 0.98560028 * js) % 360;
    const C = 1.9148 * Math.sin(M * rad) + 0.02 * Math.sin(2 * M * rad) + 0.0003 * Math.sin(3 * M * rad);
    const L = (M + C + 180 + 102.9372) % 360;
    const jt = 2451545 + js + 0.0053 * Math.sin(M * rad) - 0.0069 * Math.sin(2 * L * rad);
    const dec = Math.asin(Math.sin(L * rad) * Math.sin(23.44 * rad));
    const w = Math.acos((Math.sin(-0.833 * rad) - Math.sin(lat * rad) * Math.sin(dec)) / (Math.cos(lat * rad) * Math.cos(dec)));
    return (jt + w / (2 * Math.PI) - 2440587.5) * 864e5;
  };
  const israelDate = (t) => {
    const p = {}; new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Jerusalem', year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short' }).formatToParts(new Date(t)).forEach((x) => { p[x.type] = x.value; });
    return { y: +p.year, m: +p.month, d: +p.day, wd: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(p.weekday) };
  };
  const ilTime = (t) => new Intl.DateTimeFormat('he-IL', { timeZone: 'Asia/Jerusalem', hour: '2-digit', minute: '2-digit' }).format(new Date(t));

  class ShabbatAuto extends HTMLElement {
    connectedCallback() {
      if (this.ready) return; // re-attached by close()
      if (window.Shopify && window.Shopify.designMode && !location.search.includes('shabbat=1')) return;
      this.ready = true;
      this.lat = Number(this.dataset.lat); this.lng = Number(this.dataset.lng);
      this.candle = Number(this.dataset.candle) * 6e4; this.havdalah = Number(this.dataset.havdalah) * 6e4;
      this.check();
    }
    range(now) {
      // The Shabbat that is current or next: Friday sunset − candle → Saturday sunset + havdalah
      const t = israelDate(now);
      const back = t.wd === 6 ? 1 : t.wd === 5 ? 0 : -((5 - t.wd + 7) % 7);
      const fri = new Date(Date.UTC(t.y, t.m - 1, t.d - back));
      const sat = new Date(fri.getTime() + 864e5);
      const start = sunsetUTC(fri.getUTCFullYear(), fri.getUTCMonth() + 1, fri.getUTCDate(), this.lat, this.lng) - this.candle;
      const end = sunsetUTC(sat.getUTCFullYear(), sat.getUTCMonth() + 1, sat.getUTCDate(), this.lat, this.lng) + this.havdalah;
      if (now > end) { // Saturday night after havdalah → next week
        return this.range(now + 2 * 864e5);
      }
      return { start, end };
    }
    check() {
      clearTimeout(this.next);
      const now = Date.now();
      const force = location.search.includes('shabbat=1');
      const { start, end } = this.range(now);
      if (force || (now >= start && now < end)) return this.close(force ? now + 36e5 : end);
      this.open();
      // Friday notice + exact switch at candle lighting
      const left = start - now;
      if (this.dataset.notice === 'true' && left < 12 * 36e5) this.notice(start, end);
      if (left < 864e5) this.next = setTimeout(() => this.check(), left + 500);
    }
    close(end) {
      this.hidden = false;
      const screen = $('[data-screen]', this); screen.hidden = false;
      $('[data-notice-bar]', this).hidden = true;
      document.documentElement.classList.add('shabbat-on');
      if (document.body.lastElementChild !== this) document.body.appendChild(this);
      [...document.body.children].forEach((c) => { if (c !== this && c.tagName !== 'SCRIPT') c.inert = true; });
      const timer = $('shabbat-timer', this);
      if (timer) timer.start(end);
      this.addEventListener('shabbat:end', () => setTimeout(() => location.reload(), 4000), { once: true });
    }
    open() {
      $('[data-screen]', this).hidden = true;
      if (document.documentElement.classList.contains('shabbat-on')) [...document.body.children].forEach((c) => { c.inert = false; });
      document.documentElement.classList.remove('shabbat-on');
    }
    notice(start, end) {
      try { if (sessionStorage.getItem('v-shab-notice') === String(start)) return; } catch (e) { /* noop */ }
      const bar = $('[data-notice-bar]', this);
      $('[data-notice-msg]', bar).textContent = this.dataset.noticeText.replace('[[start]]', ilTime(start)).replace('[[end]]', ilTime(end));
      this.hidden = false; bar.hidden = false;
      $('[data-notice-close]', bar).addEventListener('click', () => { bar.hidden = true; try { sessionStorage.setItem('v-shab-notice', String(start)); } catch (e) { /* noop */ } }, { once: true });
    }
  }
  customElements.define('shabbat-auto', ShabbatAuto);

  /* ---------- predictive search ---------- */
  /* Hebrew → English search terms (product titles are in English). Prefix-matched while typing. */
  const SEARCH_TERMS = {
    'שחור': 'black', 'לבן': 'white', 'אדום': 'red', 'בורדו': 'burgundy', 'יין': 'wine', 'חום': 'brown', 'בז': 'beige', 'בז׳': 'beige',
    'קרם': 'cream', 'שמנת': 'cream', 'ירוק': 'green', 'זית': 'olive', 'מנטה': 'mint', 'מאצה': 'matcha', 'מאצ׳ה': 'matcha', 'סגול': 'purple',
    'לילך': 'lilac', 'ורוד': 'pink', 'פוקסיה': 'fuchsia', 'כחול': 'blue', 'נייבי': 'navy', 'תכלת': 'skyblue', 'צהוב': 'yellow',
    'זהב': 'gold', 'כסף': 'silver', 'אפור': 'gray', 'כתום': 'orange', 'טורקיז': 'turquoise',
    'שמלה': 'dress', 'שמלות': 'dress', 'שמלת': 'dress', 'מקסי': 'maxi', 'מיני': 'mini', 'מידי': 'midi', 'סט': 'set', 'סטים': 'set',
    'תיק': 'bag', 'תיקים': 'bag', 'תחרה': 'lace', 'סאטן': 'satin', 'רומא': 'roma', 'אתנה': 'athena', 'פלורה': 'flora',
    'מרגריטה': 'margarita', 'קורנליה': 'cornelia', 'ויוה': 'viva', 'רויאל': 'royal', 'אלסיה': 'alessia', 'שקוף': 'sheer', 'ריב': 'ribbed'
  };
  const hebrewTerms = (q) => {
    const out = []; let mapped = q; let changed = false;
    q.split(/\s+/).forEach((tok) => {
      if (!/[֐-׿]/.test(tok)) return;
      const t = tok.replace(/['"׳״]/g, '');
      const keys = Object.keys(SEARCH_TERMS).filter((k) => k.replace(/['"׳״]/g, '').startsWith(t));
      if (!keys.length) return;
      keys.sort((a, b) => a.length - b.length);
      keys.slice(0, 4).forEach((k) => { if (!out.find((o) => o.en === SEARCH_TERMS[k])) out.push({ he: k, en: SEARCH_TERMS[k] }); });
      mapped = mapped.replace(tok, SEARCH_TERMS[keys[0]]); changed = true;
    });
    return { mapped: changed ? mapped : null, chips: out };
  };

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"]', this);
      this.results = $('[data-results]', this);
      this.defaults = $('[data-default]', this);
      this.instant = $('[data-instant]', this);
      this.input.addEventListener('input', () => { this.renderChips(); this.debounced(); });
      this.debounced = debounce(() => this.search(), 170);
      const dlg = this.closest('dialog');
      if (dlg) dlg.addEventListener('dialog:open', () => setTimeout(() => this.input.focus(), 60));
    }
    renderChips() {
      if (!this.instant) return;
      const q = this.input.value.trim();
      const { chips } = hebrewTerms(q);
      const base = V.routes.search;
      this.instant.innerHTML = chips.map((c) => `<a class="pill" href="${base}?q=${encodeURIComponent(c.en)}&options%5Bprefix%5D=last">${c.he} · ${c.en}</a>`).join('');
    }
    url(q) {
      return `${V.routes.predictive}?q=${encodeURIComponent(q)}&resources[type]=product,collection,query,page&resources[limit]=10&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor,tag&section_id=predictive-search`;
    }
    async search() {
      const q = this.input.value.trim();
      if (!q) { this.results.innerHTML = ''; this.defaults.hidden = false; this.input.setAttribute('aria-expanded', 'false'); return; }
      if (this.ctrl) this.ctrl.abort();
      this.ctrl = new AbortController();
      const { signal } = this.ctrl;
      const { mapped } = hebrewTerms(q);
      try {
        const reqs = [fetch(this.url(q), { signal }).then((r) => r.text())];
        if (mapped && mapped !== q) reqs.push(fetch(this.url(mapped), { signal }).then((r) => r.text()));
        const docs = (await Promise.all(reqs)).map((h) => $('.shopify-section', parseHTML(h))).filter(Boolean);
        // Prefer the response with products; merge unique product cards from the other one
        docs.sort((a, b) => $$('.card', b).length - $$('.card', a).length);
        const main = docs[0];
        if (main && docs[1]) {
          let grid = $('.grid-products', main);
          const seen = new Set($$('.card', main).map((c) => c.dataset.handle));
          $$('.card', docs[1]).forEach((c) => { if (grid && !seen.has(c.dataset.handle) && seen.size < 12) { grid.appendChild(c); seen.add(c.dataset.handle); } });
        }
        this.results.innerHTML = main ? main.innerHTML : '';
        this.defaults.hidden = true;
        this.input.setAttribute('aria-expanded', 'true');
        Wishlist.paint(this.results);
      } catch (e) { /* aborted */ }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------- wishlist (localStorage) + share ---------- */
  const productCache = {};
  const productJson = async (handle) => {
    if (productCache[handle]) return productCache[handle];
    try {
      const res = await fetch(`${V.routes.root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}.js`);
      if (!res.ok) return null;
      productCache[handle] = await res.json();
      return productCache[handle];
    } catch (e) { return null; }
  };
  const imgUrl = (src, w) => { if (!src) return ''; const u = src.startsWith('//') ? `https:${src}` : src; return `${u}${u.includes('?') ? '&' : '?'}width=${w}`; };
  const esc = (t) => String(t ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const Wishlist = {
    key: 'v-wish',
    all() { return store.get(this.key, []); },
    has(h) { return this.all().includes(h); },
    save(list) { store.set(this.key, list.slice(0, 60)); this.paint(); this.count(); document.dispatchEvent(new CustomEvent('wishlist:change')); },
    toggle(h) {
      const list = this.all();
      const i = list.indexOf(h);
      if (i > -1) list.splice(i, 1); else list.unshift(h);
      this.save(list);
      V.toast(i > -1 ? V.strings.wishRemoved : V.strings.wishAdded);
    },
    merge(handles) { const list = this.all(); handles.forEach((h) => { if (!list.includes(h)) list.push(h); }); this.save(list); },
    paint(root = document) {
      const list = this.all();
      $$('[data-wish]', root).forEach((b) => { const on = list.includes(b.dataset.wish); b.classList.toggle('is-active', on); b.setAttribute('aria-pressed', String(on)); });
    },
    count() {
      const n = this.all().length;
      $$('[data-wish-count]').forEach((b) => { b.textContent = n; b.dataset.count = n; });
      $$('[data-wish-count-label]').forEach((b) => { b.textContent = n ? `(${n})` : ''; });
    },
    shareUrl(list = this.all()) { return `${window.location.origin}${V.routes.root.replace(/\/$/, '')}/pages/wishlist?items=${list.map(encodeURIComponent).join(',')}`; },
    async share() {
      const url = this.shareUrl();
      const data = { title: V.strings.wishShareTitle, text: V.strings.wishShareText, url };
      if (navigator.share) { try { await navigator.share(data); return; } catch (e) { if (e && e.name === 'AbortError') return; } }
      try { await navigator.clipboard.writeText(url); V.toast(V.strings.copied); } catch (e) { window.prompt('', url); }
    },
    shareWhatsApp() { window.open(`https://wa.me/?text=${encodeURIComponent(`${V.strings.wishShareText} ${this.shareUrl()}`)}`, '_blank', 'noopener'); }
  };
  V.wishlist = Wishlist;
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-wish]');
    if (b) { e.preventDefault(); Wishlist.toggle(b.dataset.wish); return; }
    if (e.target.closest('[data-wish-share]')) { Wishlist.share(); return; }
    if (e.target.closest('[data-wish-share-wa]')) { Wishlist.shareWhatsApp(); return; }
    const rm = e.target.closest('[data-wish-remove]');
    if (rm) { const list = Wishlist.all().filter((h) => h !== rm.dataset.wishRemove); Wishlist.save(list); }
  });

  const fetchCard = async (handle) => {
    try {
      const res = await fetch(`${V.routes.root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}?section_id=card-render`);
      if (res.ok) { const card = $('.card', parseHTML(await res.text())); if (card) return card; }
    } catch (e) { /* fall back */ }
    // Fallback: build a simple card from the product JSON (works even without the product template)
    const p = await productJson(handle);
    if (!p) return null;
    const el = document.createElement('div');
    el.className = 'card'; el.dataset.card = ''; el.dataset.handle = p.handle;
    const sale = p.compare_at_price > p.price;
    el.innerHTML = `<div class="card__media">${p.featured_image ? `<img src="${imgUrl(p.featured_image, 700)}" alt="${esc(p.title)}" loading="lazy" width="700" height="933">` : ''}
      <button type="button" class="icon-btn glass card__wish" data-wish="${esc(p.handle)}" aria-pressed="false" aria-label="${esc(V.strings.wishRemove)}"><svg class="icon icon--heart" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M12 20.5s-7.5-4.6-9.2-9.3C1.6 7.9 3.7 4.5 7.1 4.5c2 0 3.6 1.1 4.9 2.9 1.3-1.8 2.9-2.9 4.9-2.9 3.4 0 5.5 3.4 4.3 6.7C19.5 15.9 12 20.5 12 20.5Z"/></svg></button></div>
      <div class="card__info"><a class="card__title" href="${esc(p.url)}">${esc(p.title)}</a><div class="price${sale ? ' price--sale' : ''}"><span class="price__current">${V.formatMoney(p.price)}</span>${sale ? `<s class="price__compare">${V.formatMoney(p.compare_at_price)}</s>` : ''}</div></div>`;
    return el;
  };

  // Drawer list: compact rows from product JSON
  class WishlistList extends HTMLElement {
    connectedCallback() {
      document.addEventListener('wishlist:change', () => this.render());
      const d = this.closest('dialog'); if (d) d.addEventListener('dialog:open', () => this.render());
      this.render();
    }
    async render() {
      const list = Wishlist.all();
      const empty = document.getElementById(this.dataset.empty);
      const foot = $('[data-wish-foot]', this.closest('dialog') || document);
      if (empty) empty.hidden = list.length > 0;
      if (foot) foot.hidden = list.length === 0;
      Wishlist.count();
      if (!list.length) { this.innerHTML = ''; return; }
      const items = (await Promise.all(list.map(productJson))).filter(Boolean);
      this.innerHTML = items.map((p) => {
        const single = p.variants && p.variants.length === 1;
        const action = !p.available
          ? `<span class="badge badge--sold">${esc(V.strings.soldOutShort)}</span>`
          : single
            ? `<button type="button" class="icon-btn icon-btn--ink" data-quick-add="${p.variants[0].id}" aria-label="${esc(V.strings.addToCart)}"><svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M5 8h14l-1 12.2a1 1 0 0 1-1 .8H7a1 1 0 0 1-1-.8L5 8Z"/><path d="M9 10V6.5a3 3 0 0 1 6 0V10"/></svg></button>`
            : `<button type="button" class="icon-btn icon-btn--ink" data-quick-view="${esc(p.url)}" aria-label="${esc(V.strings.chooseSize)}"><svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M5 8h14l-1 12.2a1 1 0 0 1-1 .8H7a1 1 0 0 1-1-.8L5 8Z"/><path d="M9 10V6.5a3 3 0 0 1 6 0V10"/></svg></button>`;
        const sale = p.compare_at_price > p.price;
        return `<div class="wish-item">
          <div class="wish-item__img">${p.featured_image ? `<img src="${imgUrl(p.featured_image, 240)}" alt="" loading="lazy" width="240" height="320">` : ''}</div>
          <div><a class="wish-item__title" href="${esc(p.url)}">${esc(p.title)}</a>
            <div class="price small${sale ? ' price--sale' : ''}"><span class="price__current">${V.formatMoney(p.price)}</span>${sale ? `<s class="price__compare">${V.formatMoney(p.compare_at_price)}</s>` : ''}</div></div>
          <div class="wish-item__actions">${action}
            <button type="button" class="icon-btn" data-wish-remove="${esc(p.handle)}" aria-label="${esc(V.strings.wishRemove)}: ${esc(p.title)}"><svg class="icon icon--s" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg></button></div>
        </div>`;
      }).join('');
    }
  }
  customElements.define('wishlist-list', WishlistList);

  // Page grid: own list, or a shared list from ?items=
  class WishlistGrid extends HTMLElement {
    connectedCallback() {
      const params = new URLSearchParams(window.location.search);
      this.shared = (params.get('items') || '').split(',').map((h) => h.trim()).filter(Boolean);
      const banner = $('[data-wish-shared-banner]');
      if (this.shared.length && banner) {
        banner.hidden = false;
        const note = $('[data-wish-note]'); if (note) note.hidden = true;
        $('[data-wish-import]', banner).addEventListener('click', () => { Wishlist.merge(this.shared); V.toast(V.strings.imported); });
      }
      this.render();
      if (!this.shared.length) document.addEventListener('wishlist:change', () => this.render());
    }
    async render() {
      const list = this.shared.length ? this.shared : Wishlist.all();
      const empty = document.getElementById(this.dataset.emptyTarget);
      const bar = $('[data-wish-toolbar]');
      if (empty) empty.hidden = list.length > 0;
      if (bar) bar.hidden = this.shared.length > 0 || list.length === 0;
      const cards = await Promise.all(list.map(fetchCard));
      this.innerHTML = '';
      cards.filter(Boolean).forEach((c) => this.appendChild(c));
      Wishlist.paint(this);
    }
  }
  customElements.define('wishlist-grid', WishlistGrid);

  /* ---------- customer videos (UGC) player ---------- */
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-ugc-open]');
    if (!t) return;
    const dlg = $('#UgcDialog'); if (!dlg) return;
    const v = $('[data-ugc-video]', dlg); const info = $('[data-ugc-info]', dlg);
    v.src = t.dataset.src; if (t.dataset.poster) v.poster = t.dataset.poster; else v.removeAttribute('poster');
    const parts = [];
    if (t.dataset.name) parts.push(`<strong>${esc(t.dataset.name)}</strong>${t.dataset.handle ? ` <a href="https://instagram.com/${esc(t.dataset.handle)}" target="_blank" rel="noopener">@${esc(t.dataset.handle)}</a>` : ''}`);
    if (t.dataset.productUrl) parts.push(`<a href="${esc(t.dataset.productUrl)}">${esc(t.dataset.productTitle)}</a>`);
    info.innerHTML = parts.map((x) => `<span>${x}</span>`).join(''); info.hidden = !parts.length;
    Dialogs.open(dlg, t);
    v.muted = false; v.play().catch(() => {});
    dlg.addEventListener('dialog:close', () => { v.pause(); v.removeAttribute('src'); v.load(); }, { once: true });
  });

  /* ---------- recently viewed ---------- */
  const page = $('[data-product-page]');
  if (page && page.dataset.handle) {
    const list = store.get('v-recent', []).filter((h) => h !== page.dataset.handle);
    list.unshift(page.dataset.handle);
    store.set('v-recent', list.slice(0, 12));
  }
  class RecentlyViewed extends HTMLElement {
    async connectedCallback() {
      const limit = Number(this.dataset.limit) || 4;
      const list = store.get('v-recent', []).filter((h) => h !== this.dataset.exclude).slice(0, limit);
      if (!list.length) return;
      const cards = (await Promise.all(list.map(fetchCard))).filter(Boolean);
      if (!cards.length) return;
      cards.forEach((c) => this.appendChild(c));
      const wrap = this.closest('[data-rv-wrap]'); if (wrap) wrap.hidden = false;
      Wishlist.paint(this);
    }
  }
  customElements.define('recently-viewed', RecentlyViewed);

  /* ---------- product recommendations ---------- */
  class ProductRecs extends HTMLElement {
    connectedCallback() {
      if (this.children.length) return;
      const io = new IntersectionObserver(async ([en]) => {
        if (!en.isIntersecting) return;
        io.disconnect();
        try {
          const html = await (await fetch(this.dataset.url)).text();
          const fresh = $('product-recs', parseHTML(html));
          if (fresh && fresh.innerHTML.trim()) { this.innerHTML = fresh.innerHTML; Wishlist.paint(this); }
        } catch (e) { /* ignore */ }
      }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
  }
  customElements.define('product-recs', ProductRecs);

  /* ---------- collection filters / sort / load more ---------- */
  class CollectionView extends HTMLElement {
    connectedCallback() {
      this.addEventListener('submit', (e) => { if (e.target.matches('[data-filter-form]')) { e.preventDefault(); this.apply(this.formUrl(e.target)); } });
      this.addEventListener('change', (e) => { if (e.target.matches('[data-sort]')) { const f = $('[data-filter-form]', this); this.apply(this.formUrl(f)); } });
      this.addEventListener('click', (e) => {
        const a = e.target.closest('[data-filter-link]');
        if (a) { e.preventDefault(); this.apply(a.href); return; }
        const more = e.target.closest('[data-load-more]');
        if (more) { e.preventDefault(); this.loadMore(more); }
      });
      window.addEventListener('popstate', () => this.apply(window.location.href, false));
    }
    formUrl(form) {
      const fd = new FormData(form);
      const sort = $('[data-sort]', this);
      if (sort) fd.set('sort_by', sort.value);
      const params = new URLSearchParams();
      for (const [k, v] of fd.entries()) if (v !== '') params.append(k, v);
      return `${form.action.split('?')[0]}?${params.toString()}`;
    }
    async fetchSection(url) {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('section_id', this.dataset.section);
      return parseHTML(await (await fetch(u.toString())).text());
    }
    async apply(url, push = true) {
      const grid = $('[data-grid]', this);
      if (grid) grid.classList.add('is-loading');
      const drawer = $('#FilterDrawer');
      if (drawer && drawer.open) Dialogs.close(drawer);
      try {
        const doc = await this.fetchSection(url);
        const fresh = $('collection-view', doc);
        if (!fresh) { window.location.href = url; return; }
        setTimeout(() => {
          ['[data-facets]', '[data-grid]', '[data-pagination]'].forEach((sel) => {
            const a = $(sel, this); const b = $(sel, fresh);
            if (a && b) a.replaceWith(b); else if (a && !b) a.remove(); else if (!a && b) this.appendChild(b);
          });
          Wishlist.paint(this);
        }, drawer && drawer.open ? 300 : 0);
        if (push) window.history.pushState({}, '', url);
      } catch (e) { window.location.href = url; }
    }
    async loadMore(btn) {
      btn.classList.add('is-loading');
      try {
        const doc = await this.fetchSection(btn.href);
        const grid = $('[data-grid]', this);
        $$('[data-grid] > *', doc).forEach((c) => grid.appendChild(c));
        const pag = $('[data-pagination]', doc);
        const cur = $('[data-pagination]', this);
        if (pag && cur) cur.replaceWith(pag);
        Wishlist.paint(grid);
      } catch (e) { window.location.href = btn.href; }
    }
  }
  customElements.define('collection-view', CollectionView);

  /* ---------- reels ---------- */
  class VReels extends HTMLElement {
    connectedCallback() {
      this.videos = $$('video[data-reel]', this);
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          const v = en.target;
          if (en.isIntersecting) {
            $$('source[data-src]', v).forEach((s) => { s.src = s.dataset.src; s.removeAttribute('data-src'); v.load(); });
            if (!reducedMotion()) v.play().catch(() => {});
          } else v.pause();
        });
      }, { threshold: 0.4 });
      this.videos.forEach((v) => io.observe(v));
      this.addEventListener('click', (e) => {
        const b = e.target.closest('[data-reel-sound]');
        if (!b) return;
        const v = $('video', b.closest('.reel__media'));
        this.videos.forEach((o) => { if (o !== v) o.muted = true; });
        v.muted = !v.muted;
        if (!v.muted) v.play().catch(() => {});
        $$('[data-reel-sound]', this).forEach((x) => x.setAttribute('aria-pressed', 'false'));
        b.setAttribute('aria-pressed', String(!v.muted));
      });
    }
  }
  customElements.define('v-reels', VReels);

  /* ---------- shop the look ---------- */
  class ShopTheLook extends HTMLElement {
    connectedCallback() {
      const items = $$('[data-look-item]', this);
      const spots = $$('[data-hotspot]', this);
      const activate = (i, scroll) => {
        items.forEach((it) => it.classList.toggle('is-active', it.dataset.lookItem === String(i)));
        spots.forEach((s) => s.setAttribute('aria-pressed', String(s.dataset.hotspot === String(i))));
        const it = items.find((x) => x.dataset.lookItem === String(i));
        if (scroll && it && !isDesktop()) it.scrollIntoView({ block: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
      };
      spots.forEach((s) => s.addEventListener('click', () => activate(s.dataset.hotspot, true)));
      items.forEach((it) => it.addEventListener('pointerenter', () => activate(it.dataset.lookItem, false)));
    }
  }
  customElements.define('shop-the-look', ShopTheLook);

  /* ---------- accessibility panel ---------- */
  class A11yPanel extends HTMLElement {
    connectedCallback() {
      this.state = store.get('v-a11y', {});
      this.out = $('[data-a11y-scale-out]', this);
      this.paint();
      this.addEventListener('click', (e) => {
        const t = e.target.closest('[data-a11y]');
        const sc = e.target.closest('[data-a11y-scale]');
        if (t) { const k = t.dataset.a11y; this.state[k] = !this.state[k]; this.save(); }
        else if (sc) {
          const cur = Number(this.state.scale) || 1;
          this.state.scale = Math.min(1.6, Math.max(0.9, Math.round((cur + Number(sc.dataset.a11yScale) * 0.1) * 10) / 10));
          this.save();
        } else if (e.target.closest('[data-a11y-reset]')) { this.state = {}; this.save(); }
      });
    }
    save() { store.set('v-a11y', this.state); this.paint(); }
    paint() {
      const html = document.documentElement;
      ['contrast', 'grayscale', 'links', 'readable', 'still', 'cursor', 'focus'].forEach((k) => {
        html.classList.toggle(`a11y-${k}`, !!this.state[k]);
        const b = $(`[data-a11y="${k}"]`, this); if (b) b.setAttribute('aria-pressed', String(!!this.state[k]));
      });
      const s = Number(this.state.scale) || 1;
      html.style.setProperty('--a11y-scale', s);
      if (this.out) this.out.textContent = `${Math.round(s * 100)}%`;
    }
  }
  customElements.define('a11y-panel', A11yPanel);

  /* ---------- cookie banner (+ Shopify Customer Privacy API) ---------- */
  class CookieBanner extends HTMLElement {
    connectedCallback() {
      if (window.Shopify && window.Shopify.designMode) return;
      const saved = store.get('v-consent', null);
      if (saved !== null) return;
      this.hidden = false;
      const root = document.documentElement;
      const setH = () => root.style.setProperty('--cookie-h', `${this.firstElementChild.offsetHeight + 12}px`);
      requestAnimationFrame(setH); window.addEventListener('resize', setH);
      this.addEventListener('click', (e) => {
        const b = e.target.closest('[data-consent]');
        if (!b) return;
        const yes = b.dataset.consent === 'yes';
        store.set('v-consent', yes);
        const set = () => {
          const cp = window.Shopify && window.Shopify.customerPrivacy;
          if (cp && cp.setTrackingConsent) cp.setTrackingConsent({ analytics: yes, marketing: yes, preferences: yes, sale_of_data: yes }, () => {});
        };
        if (window.Shopify && window.Shopify.loadFeatures) window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], set); else set();
        this.hidden = true;
        root.style.removeProperty('--cookie-h');
      });
    }
  }
  customElements.define('cookie-banner', CookieBanner);

  /* ---------- sign-up popup ---------- */
  class SignupPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = $('dialog', this);
      const posted = window.location.search.includes('customer_posted=true') || window.location.hash === '#PopupForm';
      if (posted && $('.popup__code, .form-msg--err', this.dialog)) { Dialogs.open(this.dialog); return; }
      if (window.Shopify && window.Shopify.designMode) return;
      const last = store.get('v-popup', 0);
      const days = Number(this.dataset.days) || 7;
      if (last && Date.now() - last < days * 864e5) return;
      setTimeout(() => {
        if ($$('dialog[open]').length) return;
        Dialogs.open(this.dialog);
        store.set('v-popup', Date.now());
      }, (Number(this.dataset.delay) || 8) * 1000);
    }
  }
  customElements.define('signup-popup', SignupPopup);

  /* ---------- drop countdown banner ---------- */
  class VCountdownBanner extends HTMLElement {
    connectedCallback() {
      this.end = new Date(this.dataset.end).getTime();
      if (!this.end || Number.isNaN(this.end)) return;
      this.el = { d: $('[data-d]', this), h: $('[data-h]', this), m: $('[data-m]', this), s: $('[data-s]', this) };
      this.tick(); this.timer = setInterval(() => this.tick(), 1000);
    }
    tick() {
      const d = this.end - Date.now();
      if (d <= 0) {
        clearInterval(this.timer);
        if (this.dataset.hide === 'true') { this.hidden = true; return; }
        $('[data-clock]', this).hidden = true; $('[data-done]', this).hidden = false; return;
      }
      this.el.d.textContent = pad(Math.floor(d / 864e5)); this.el.h.textContent = pad(Math.floor((d % 864e5) / 36e5));
      this.el.m.textContent = pad(Math.floor((d % 36e5) / 6e4)); this.el.s.textContent = pad(Math.floor((d % 6e4) / 1e3));
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('v-countdown-banner', VCountdownBanner);

  /* ---------- estimated delivery (business days, Israel: no Fri/Sat) ---------- */
  class VEta extends HTMLElement {
    connectedCallback() {
      const nums = (t) => (String(t).match(/\d+/g) || []).map(Number);
      const [p1, p2 = p1] = nums(this.dataset.proc); const [s1, s2 = s1] = nums(this.dataset.ship);
      if (!p1 && !s1) return;
      const addBiz = (n) => { const d = new Date(); let left = n; while (left > 0) { d.setDate(d.getDate() + 1); const w = d.getDay(); if (w !== 5 && w !== 6) left -= 1; } return d; };
      const fmt = (d) => d.toLocaleDateString('he-IL', { weekday: 'short', day: 'numeric', month: 'numeric' });
      const out = $('[data-eta-range]', this);
      if (out) out.textContent = `${fmt(addBiz((p1 || 0) + (s1 || 0)))} – ${fmt(addBiz((p2 || 0) + (s2 || 0)))}`;
    }
  }
  customElements.define('v-eta', VEta);

  /* ---------- collection grid density ---------- */
  const applyDensity = (val) => {
    $$('[data-density]').forEach((g) => { g.dataset.density = val; });
    $$('[data-density-set]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.densitySet === val)));
  };
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-density-set]');
    if (b) { store.set('v-density', b.dataset.densitySet); applyDensity(b.dataset.densitySet); }
  });
  document.addEventListener('DOMContentLoaded', () => applyDensity(store.get('v-density', 'small')));
  new MutationObserver(() => { const g = $('[data-density]'); if (g && g.dataset.density === '') applyDensity(store.get('v-density', 'small')); })
    .observe(document.documentElement, { childList: true, subtree: true });

  /* ---------- cart attributes (gift wrap) ---------- */
  document.addEventListener('change', (e) => {
    const c = e.target.closest('[data-cart-attr]');
    if (!c) return;
    fetch(`${V.routes.cartUpdate}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { [c.dataset.cartAttr]: c.checked ? 'כן' : '' } }) });
  });

  /* ---------- carousel arrows (‹ title ›) ---------- */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-scroll-prev], [data-scroll-next]');
    if (!b) return;
    const track = document.getElementById(b.dataset.scrollPrev || b.dataset.scrollNext);
    if (!track) return;
    const rtl = getComputedStyle(track).direction === 'rtl';
    const step = track.clientWidth * 0.9 * (b.dataset.scrollNext ? 1 : -1) * (rtl ? -1 : 1);
    track.scrollBy({ left: step, behavior: reducedMotion() ? 'auto' : 'smooth' });
  });

  /* ---------- back to top ---------- */
  const toTop = $('[data-to-top]');
  if (toTop) {
    window.addEventListener('scroll', debounce(() => { toTop.hidden = window.scrollY < window.innerHeight * 1.5; }, 80), { passive: true });
    toTop.addEventListener('click', () => { window.scrollTo({ top: 0, behavior: reducedMotion() ? 'auto' : 'smooth' }); const s = $('.skip-link'); if (s) $('#MainContent').focus({ preventScroll: true }); });
  }

  /* ---------- misc: share, copy, reveal, footer accordions, login recover ---------- */
  document.addEventListener('click', async (e) => {
    const share = e.target.closest('[data-share]');
    if (share) {
      const data = { title: share.dataset.title, url: share.dataset.url };
      if (navigator.share) { try { await navigator.share(data); } catch (er) { /* dismissed */ } }
      else { try { await navigator.clipboard.writeText(data.url); V.toast(V.strings.copied); } catch (er) { /* noop */ } }
      return;
    }
    const copy = e.target.closest('[data-copy]');
    if (copy) { try { await navigator.clipboard.writeText(copy.dataset.copy); V.toast(V.strings.copied); } catch (er) { /* noop */ } return; }
    const rec = e.target.closest('[data-toggle-recover]');
    if (rec) {
      e.preventDefault();
      const r = $('[data-recover-view]'); const l = $('[data-login-view]');
      if (r && l) { r.hidden = !r.hidden; l.hidden = !r.hidden; }
    }
  });

  const init = () => {
    Wishlist.paint(); Wishlist.count();
    if (window.location.hash === '#recover') { const r = $('[data-recover-view]'); const l = $('[data-login-view]'); if (r && l) { r.hidden = false; l.hidden = true; } }
    if (!isDesktop()) $$('[data-footer-acc]').forEach((d) => d.removeAttribute('open'));
    const reveals = $$('.reveal');
    if ('IntersectionObserver' in window && reveals.length) {
      const io = new IntersectionObserver((ens) => ens.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } }), { rootMargin: '0px 0px -8% 0px' });
      reveals.forEach((r) => io.observe(r));
    } else reveals.forEach((r) => r.classList.add('is-in'));
    // keep the cart drawer fresh when returning via bfcache
    window.addEventListener('pageshow', (ev) => { if (ev.persisted) Cart.refreshDrawer().then(() => fetch(`${V.routes.cart}.js`).then((r) => r.json()).then((c) => Cart.setCount(c.item_count))); });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();

  /* Theme editor: re-init after section reloads */
  document.addEventListener('shopify:section:load', () => { Wishlist.paint(); $$('.reveal').forEach((r) => r.classList.add('is-in')); });
})();
